import type { LanguageCode } from './languages'

type Localized = Record<LanguageCode, string>

export type CafePage = {
  slug: string
  eyebrow: Localized
  title: Localized
  description: Localized
  image: string
  highlights: Localized[]
  sections: Array<{
    title: Localized
    copy: Localized
  }>
}

const text = (tr: string, en: string, ru: string): Localized => ({ tr, en, ru })

export const cafePages: CafePage[] = [
  {
    slug: 'kahvaltilar',
    eyebrow: text('Kahvalti', 'Breakfast', 'Breakfast'),
    title: text('Termal sabahlarina sicak bir baslangic', 'A warm start to Thermal mornings', 'Breakfast'),
    description: text(
      'Modi Cafe kahvaltilari, taze urunler ve rahat servisle Yalova Termal gezisini keyifli bir molaya donusturur.',
      'Modi Cafe breakfasts turn a Yalova Thermal visit into a relaxed stop with fresh products and warm service.',
      'Modi Cafe kahvalti ve cafe deneyimi.',
    ),
    image: '/legacy-media/wp-content/uploads/2025/02/kahvaltilar-1024x768.png',
    highlights: [
      text('Taze tabaklar', 'Fresh plates', 'Fresh plates'),
      text('Paylasimli kahvalti', 'Shared breakfast', 'Shared breakfast'),
      text('Sicak servis', 'Warm service', 'Warm service'),
    ],
    sections: [
      {
        title: text('Sade, doyurucu, samimi', 'Simple, filling, friendly', 'Simple and friendly'),
        copy: text(
          'Kahvalti menusu kolay secilen, gunluk cafe akisina uygun ve samimi bir servis diliyle hazirlanir.',
          'The breakfast menu is designed to be easy to choose from and comfortable for everyday cafe flow.',
          'Kahvalti menusu guncel olarak sunulur.',
        ),
      },
    ],
  },
  {
    slug: 'sicak-icecekler',
    eyebrow: text('Kahve & Cay', 'Coffee & Tea', 'Coffee & Tea'),
    title: text('Kahve kokusu merkeze yerlessin', 'Let coffee set the tone', 'Coffee'),
    description: text(
      'Espresso bazli icecekler, Turk kahvesi ve bitki caylariyla Modi Cafe daha sakin, daha sicak bir durak.',
      'With espresso drinks, Turkish coffee and herbal teas, Modi Cafe is a warmer and calmer stop.',
      'Sicak icecek secenekleri.',
    ),
    image: '/legacy-media/wp-content/uploads/2025/02/Sicak-icecekler-1024x768.png',
    highlights: [
      text('Turk kahvesi', 'Turkish coffee', 'Turkish coffee'),
      text('Espresso bazli secimler', 'Espresso choices', 'Espresso choices'),
      text('Bitki caylari', 'Herbal teas', 'Herbal teas'),
    ],
    sections: [
      {
        title: text('Kisa mola ritmi', 'A short-break rhythm', 'Short break'),
        copy: text(
          'Termal bolgesinde hizli ama ozenli bir kahve molasi isteyen misafirler icin net ve sicak bir kategori.',
          'A clear, warm category for guests who want a quick but thoughtful coffee break in Thermal.',
          'Kahve molasi icin sakin bir secenek.',
        ),
      },
    ],
  },
  {
    slug: 'soguk-icecekler',
    eyebrow: text('Ferah', 'Fresh', 'Fresh'),
    title: text('Termal gezisine serin bir ara', 'A cool pause during a Thermal visit', 'Cold drinks'),
    description: text(
      'Soguk icecekler, limonatalar ve mevsimlik secimler Modi Cafe nin hafif ve ferah tarafini tasir.',
      'Cold drinks, lemonades and seasonal choices carry the lighter, fresher side of Modi Cafe.',
      'Soguk icecek secenekleri.',
    ),
    image: '/legacy-media/wp-content/uploads/2025/02/soguk-icecekler-1024x768.png',
    highlights: [
      text('Limonata', 'Lemonade', 'Lemonade'),
      text('Soguk kahve', 'Iced coffee', 'Iced coffee'),
      text('Mevsimlik tatlar', 'Seasonal flavors', 'Seasonal flavors'),
    ],
    sections: [
      {
        title: text('Hafif ve kolay secilir', 'Light and easy to choose', 'Easy to choose'),
        copy: text(
          'Kategori, ozellikle yaz ve hafta sonu yogunlugunda hizli siparis akisi icin sade tutuldu.',
          'The category stays simple for fast ordering, especially in summer and weekend traffic.',
          'Ferah secenekler sade bir menuyle sunulur.',
        ),
      },
    ],
  },
  {
    slug: 'tatlilar',
    eyebrow: text('Tatli & Pastane', 'Desserts', 'Desserts'),
    title: text('Kahvenin yanina tatli bir imza', 'A sweet signature beside coffee', 'Desserts'),
    description: text(
      'Gunluk tatlilar ve pastane lezzetleri, Modi Cafe nin kahve molasini tamamlayan en sicak bolumu.',
      'Daily desserts and patisserie flavors complete the coffee break at Modi Cafe.',
      'Tatli ve pastane secenekleri.',
    ),
    image: '/legacy-media/wp-content/uploads/2025/02/dondurma-1024x768.png',
    highlights: [
      text('Gunluk tatlilar', 'Daily desserts', 'Daily desserts'),
      text('Kahve eslesmeleri', 'Coffee pairings', 'Coffee pairings'),
      text('Paylasimlik lezzetler', 'Shareable flavors', 'Shareable flavors'),
    ],
    sections: [
      {
        title: text('Kucuk ama akilda kalan', 'Small, but memorable', 'Memorable'),
        copy: text(
          'Tatli sayfasi cafe ritmine uyan secili lezzetler ve kahve yanina uygun alternatiflerle hazirlanir.',
          'The dessert page is shaped as selected cafe-friendly flavors, not a huge patisserie catalog.',
          'Tatli secenekleri kahve molasini tamamlar.',
        ),
      },
    ],
  },
  {
    slug: 'hakkimizda',
    eyebrow: text('Hakkimizda', 'About', 'About'),
    title: text(
      'Yalova Termal icinde samimi bir kahvalti ve cafe duragi',
      'A friendly breakfast and cafe stop in Yalova Thermal',
      'About Modi Cafe',
    ),
    description: text(
      'Modi Cafe, Termal gezisine kahvalti, kahve ve tatli molasi eklemek isteyen misafirler icin sade, temiz ve rahat bir bulusma noktasi olarak hazirlandi.',
      'Modi Cafe is a clean and relaxed stop for guests who want breakfast, coffee and dessert during a Yalova Thermal visit.',
      'Modi Cafe, Yalova Termal icinde kahvalti ve cafe deneyimi sunar.',
    ),
    image: '/legacy-media/wp-content/uploads/2025/02/kahvaltilar-1024x768.png',
    highlights: [
      text('Yalova Termal', 'Yalova Thermal', 'Yalova Thermal'),
      text('Kahvalti & kahve', 'Breakfast & coffee', 'Breakfast & coffee'),
      text('Sade butik atmosfer', 'Simple boutique atmosphere', 'Boutique cafe'),
    ],
    sections: [
      {
        title: text('Modi Cafe ne sunar?', 'What does Modi Cafe offer?', 'What we offer'),
        copy: text(
          'Gune baslamak isteyenler icin kahvalti tabaklari, kisa mola vermek isteyenler icin sicak ve soguk icecekler, kahve yanina tatli secenekleri sunuyoruz. Amacimiz kalabalik ve karisik olmayan, kolay secilen ve rahat hissedilen bir cafe deneyimi olusturmak.',
          'We serve breakfast plates, hot and cold drinks, and desserts for coffee breaks. The aim is a calm, easy-to-choose cafe experience without unnecessary complexity.',
          'Kahvalti, kahve ve tatli molalari icin sade bir cafe deneyimi sunuyoruz.',
        ),
      },
      {
        title: text('Termal ziyaretine yakin bir mola', 'A nearby pause during your Thermal visit', 'A Thermal stop'),
        copy: text(
          'Modi Cafe, Yalova Termal bolgesinde hem bolge misafirlerinin hem de gunluk ziyaretcilerin ugrayabilecegi pratik bir durak olarak konumlanir. Menu iceriklerimiz ve duyurularimiz guncel tutulur; en dogru bilgi icin QR menu ve iletisim kanallarimizi takip edebilirsiniz.',
          'Modi Cafe is positioned as a practical stop for local guests and daily visitors in Yalova Thermal. Menus and updates are kept current through the QR menu and contact channels.',
          'Guncel menu ve duyurular QR menu uzerinden takip edilebilir.',
        ),
      },
    ],
  },
  {
    slug: 'kampanyalar',
    eyebrow: text('Kampanyalar', 'Campaigns', 'Campaigns'),
    title: text('Mevsime gore degisen cafe notlari', 'Seasonal cafe notes', 'Campaigns'),
    description: text(
      'Modi Cafe kampanyalari; kahvalti, kahve ve tatli kategorilerindeki donemsel duyurular icin sade bir alan olarak hazirlandi.',
      'Modi Cafe campaigns are prepared as a simple space for seasonal updates around breakfast, coffee and desserts.',
      'Modi Cafe kampanyalari.',
    ),
    image: '/legacy-media/wp-content/uploads/2025/02/kampanyalarimiz-1024x768.png',
    highlights: [
      text('Donemsel lezzetler', 'Seasonal flavors', 'Seasonal flavors'),
      text('Kahve eslesmeleri', 'Coffee pairings', 'Coffee pairings'),
      text('Hizli guncelleme', 'Fast updates', 'Fast updates'),
    ],
    sections: [
      {
        title: text('Admin panelden yonetilir', 'Managed from admin', 'Managed from admin'),
        copy: text(
          'Kampanya metinleri, gorseller ve menuler yeni Firebase projesi uzerinden duzenlenebilir.',
          'Campaign copy, images and menus can be edited through the new Firebase project.',
          'Kampanyalar admin panelinden guncellenir.',
        ),
      },
    ],
  },
  {
    slug: 'iletisim',
    eyebrow: text('Iletisim', 'Contact', 'Contact'),
    title: text('Modi Cafe ile iletisime gecin', 'Contact Modi Cafe', 'Contact Modi Cafe'),
    description: text(
      'Guncel menu, yol tarifi, masa bilgisi ve sorulariniz icin bizi telefon ya da WhatsApp uzerinden arayabilirsiniz.',
      'For the current menu, directions, table information and questions, you can reach us by phone or WhatsApp.',
      'Telefon ve WhatsApp uzerinden Modi Cafe ile iletisime gecebilirsiniz.',
    ),
    image: '/legacy-media/wp-content/uploads/2025/02/Sicak-icecekler-1024x768.png',
    highlights: [
      text('Yalova Termal', 'Yalova Thermal', 'Yalova Thermal'),
      text('Telefon & WhatsApp', 'Phone & WhatsApp', 'Phone & WhatsApp'),
      text('Yol tarifi ve menu', 'Directions and menu', 'Directions and menu'),
    ],
    sections: [
      {
        title: text('Bize ulasin', 'Reach us', 'Reach us'),
        copy: text(
          'Modi Cafe icin telefon, WhatsApp, sosyal medya ve harita baglantilari tek merkezden guncellenir. Bu sayfadaki bilgiler degistiginde sitedeki tum iletisim butonlari da otomatik olarak ayni bilgiyle calisir.',
          'Phone, WhatsApp, social media and map links are managed from one place. When this page changes, all contact buttons across the site use the same updated information.',
          'Iletisim bilgileri tek merkezden guncellenir.',
        ),
      },
      {
        title: text('QR menuden once kontrol edin', 'Check the QR menu first', 'Check QR menu'),
        copy: text(
          'Kahvalti, sicak icecekler, soguk icecekler ve tatli kategorileri icin en guncel icerik QR menu sayfasinda yer alir. Sorunuz olursa WhatsApp uzerinden hizlica bize yazabilirsiniz.',
          'The latest breakfast, hot drink, cold drink and dessert content is available on the QR menu page. You can also message us quickly on WhatsApp.',
          'Guncel menu QR sayfasinda yer alir.',
        ),
      },
    ],
  },
]

export function getCafePage(pathname: string) {
  const sourcePathname = pathname.replace(/^\/(en|ru)\//, '/tr/')
  const slug = sourcePathname.replace(/^\/tr\//, '').replace(/\/$/, '')
  return cafePages.find((page) => page.slug === slug) ?? null
}
